/**
 * DebugConsole — In-game HTML overlay console.
 * Toggle with tilde (~) key. Exposes window.__debug for Playwright tests.
 *
 * @module DebugConsole
 */

import DebugLog from './DebugLog.js';
import EventBus from '../core/EventBus.js';
import WorldState from '../world/WorldState.js';
import QuestSystem from '../systems/QuestSystem.js';
import FameSystem from '../systems/FameSystem.js';
import CampingSystem from '../systems/CampingSystem.js';
import VehicleSystem from '../systems/VehicleSystem.js';

// ─── Command definitions ─────────────────────────────────────────────────────

/**
 * All available debug commands.
 * Each entry: { syntax, description, handler(args, console) }
 */
const COMMANDS = {
  spawn: {
    syntax: 'spawn <enemy_id> [count]',
    description: 'Spawn enemies at party location',
    handler(args, con) {
      const [enemyId, count = 1] = args;
      if (!enemyId) return con._print('Usage: spawn <enemy_id> [count]', 'error');
      const result = window.__debug.spawn(enemyId, parseInt(count, 10));
      con._print(result ?? `Spawned ${count} × ${enemyId} at party location`, 'success');
    },
  },
  give: {
    syntax: 'give <item_id> [qty] [char_name]',
    description: 'Give item to character',
    handler(args, con) {
      const [itemId, qty = 1, charName] = args;
      if (!itemId) return con._print('Usage: give <item_id> [qty] [char_name]', 'error');
      const result = window.__debug.give(itemId, parseInt(qty, 10), charName);
      con._print(result ?? `Added ${qty} × ${itemId}${charName ? ` to ${charName}` : ''}`, 'success');
    },
  },
  setlevel: {
    syntax: 'setlevel <n> [char_name]',
    description: 'Set character level',
    handler(args, con) {
      const [n, charName] = args;
      if (!n) return con._print('Usage: setlevel <n> [char_name]', 'error');
      const result = window.__debug.setLevel(parseInt(n, 10), charName);
      con._print(result ?? `Level set to ${n}${charName ? ` for ${charName}` : ''}`, 'success');
    },
  },
  teleport: {
    syntax: 'teleport <location_id|town_name>',
    description: 'Instant teleport party',
    handler(args, con) {
      const [locationId] = args;
      if (!locationId) return con._print('Usage: teleport <location_id>', 'error');
      const result = window.__debug.teleport(locationId);
      con._print(result ?? `Teleported to ${locationId}`, 'success');
    },
  },
  settime: {
    syntax: 'settime <0-23>',
    description: 'Set in-game hour',
    handler(args, con) {
      const [hour] = args;
      if (hour === undefined) return con._print('Usage: settime <0-23>', 'error');
      const result = window.__debug.setTime(parseInt(hour, 10));
      con._print(result ?? `Time set to ${hour}:00`, 'success');
    },
  },
  setregion: {
    syntax: 'setregion <region_id> <level>',
    description: 'Change region and its level',
    handler(args, con) {
      const [regionId, level] = args;
      if (!regionId || level === undefined) return con._print('Usage: setregion <region_id> <level>', 'error');
      con._print(`setregion: Command registered (game not yet loaded)`, 'output');
    },
  },
  triggerevent: {
    syntax: 'triggerevent <event_id>',
    description: 'Fire a specific encounter',
    handler(args, con) {
      const [eventId] = args;
      if (!eventId) return con._print('Usage: triggerevent <event_id>', 'error');
      const result = window.__debug.triggerEvent(eventId);
      con._print(result ?? `Event triggered: ${eventId}`, 'success');
    },
  },

  triggerworldevent: {
    syntax: 'triggerworldevent <event_id>',
    description: 'Fire a specific world event (e.g. merchant_caravan, dragon_sighting)',
    handler(args, con) {
      const [eventId] = args;
      if (!eventId) return con._print('Usage: triggerworldevent <event_id>', 'error');
      const wes = window.__worldEventSystem;
      if (!wes) return con._print('WorldEventSystem not initialized', 'error');
      const result = wes.triggerEvent(eventId);
      con._print(result, result.startsWith('Event triggered') ? 'success' : 'error');
    },
  },

  listworldevents: {
    syntax: 'listworldevents',
    description: 'List active world events',
    handler(_args, con) {
      const wes = window.__worldEventSystem;
      if (!wes) return con._print('WorldEventSystem not initialized', 'error');
      const events = wes.getActiveEvents();
      if (!events.length) {
        con._print('No active world events.', 'output');
      } else {
        for (const e of events) {
          con._print(`  [${e.category}] ${e.id}: ${e.name}`, 'output');
        }
      }
    },
  },
  setfame: {
    syntax: 'setfame <n>',
    description: 'Set party fame (-1000 to 1000)',
    handler(args, con) {
      const [n] = args;
      if (n === undefined) return con._print('Usage: setfame <n>', 'error');
      const result = window.__debug.setFame(parseInt(n, 10));
      con._print(result ?? `Fame set to ${n}`, 'success');
    },
  },
  setalignment: {
    syntax: 'setalignment <good|neutral|evil>',
    description: 'Set party alignment',
    handler(args, con) {
      const [alignment] = args;
      if (!alignment) return con._print('Usage: setalignment <good|neutral|evil>', 'error');
      con._print(`setalignment: Command registered (game not yet loaded)`, 'output');
    },
  },
  setweather: {
    syntax: 'setweather <clear|cloudy|rainy|windy|hot|stormy|foggy|snow|blizzard|frozen>',
    description: 'Override current weather type',
    handler(args, con) {
      const [type] = args;
      if (!type) return con._print('Usage: setweather <type>', 'error');
      const ws = window.__weatherSystem;
      if (!ws) return con._print('WeatherSystem not initialized', 'error');
      const result = ws.setWeather(type);
      con._print(result, result.startsWith('Weather set') ? 'success' : 'error');
    },
  },
  setgold: {
    syntax: 'setgold <n>',
    description: 'Set party gold',
    handler(args, con) {
      const [n] = args;
      if (n === undefined) return con._print('Usage: setgold <n>', 'error');
      con._print(`setgold: Command registered (game not yet loaded)`, 'output');
    },
  },
  setvolume: {
    syntax: 'setvolume <0-100>',
    description: 'Set master audio volume (0–100)',
    handler(args, con) {
      const [n] = args;
      if (n === undefined) return con._print('Usage: setvolume <0-100>', 'error');
      const pct = parseFloat(n);
      if (isNaN(pct)) return con._print('Invalid value', 'error');
      window.__audioManager?.setVolume(pct / 100);
      con._print(`Volume set to ${Math.round(pct)}%`, 'success');
    },
  },
  mute: {
    syntax: 'mute',
    description: 'Toggle audio mute',
    handler(_args, con) {
      const am = window.__audioManager;
      if (!am) return con._print('AudioManager not ready', 'error');
      am.setMuted(!am.isMuted());
      con._print(am.isMuted() ? 'Audio muted' : 'Audio unmuted', 'success');
    },
  },
  setration: {
    syntax: 'setration <n>',
    description: 'Set party ration points',
    handler(args, con) {
      const [n] = args;
      if (n === undefined) return con._print('Usage: setration <n>', 'error');
      con._print(`setration: Command registered (game not yet loaded)`, 'output');
    },
  },
  save: {
    syntax: 'save [slot]',
    description: 'Save game to slot (default: auto)',
    handler(args, con) {
      const slot = args[0] ?? 'auto';
      const ss = window.__saveSystem;
      if (!ss) return con._print('SaveSystem not initialized', 'error');
      ss.save(slot).then(r => con._print(r.message, r.ok ? 'success' : 'error'));
    },
  },
  load: {
    syntax: 'load [slot]',
    description: 'Load game from slot (default: auto)',
    handler(args, con) {
      const slot = args[0] ?? 'auto';
      const ss = window.__saveSystem;
      if (!ss) return con._print('SaveSystem not initialized', 'error');
      ss.load(slot).then(r => con._print(r.message, r.ok ? 'success' : 'error'));
    },
  },
  saveslots: {
    syntax: 'saveslots',
    description: 'List all save slots and their metadata',
    handler(_args, con) {
      const ss = window.__saveSystem;
      if (!ss) return con._print('SaveSystem not initialized', 'error');
      const slots = ss.getSlotInfo();
      for (const s of slots) {
        if (s.empty) {
          con._print(`Slot ${s.slot}: empty`, 'output');
        } else if (s.corrupted) {
          con._print(`Slot ${s.slot}: CORRUPTED`, 'error');
        } else {
          const m = s.meta;
          const names = m?.partyNames?.join(', ') ?? '?';
          const when = m?.savedAt ? new Date(m.savedAt).toLocaleString() : '?';
          con._print(`Slot ${s.slot}: ${names} — saved ${when}`, 'output');
        }
      }
    },
  },
  achievements: {
    syntax: 'achievements',
    description: 'Show achievement progress summary',
    handler(_args, con) {
      const as = window.__achievementSystem;
      if (!as) return con._print('AchievementSystem not initialized', 'error');
      const summary = as.getSummary();
      con._print(`Achievements: ${summary.unlocked}/${summary.total} (${summary.percentage}%)`, 'success');
      for (const a of as.getUnlocked()) {
        con._print(`  ✓ ${a.name} — ${a.description}`, 'output');
      }
    },
  },

  unlockachievement: {
    syntax: 'unlockachievement <achievement_id>',
    description: 'Force-unlock an achievement by id',
    handler(args, con) {
      const [id] = args;
      if (!id) return con._print('Usage: unlockachievement <achievement_id>', 'error');
      const result = window.__debug.unlockAchievement(id);
      con._print(result ?? `Achievement unlocked: ${id}`, result?.startsWith('Achievement already') ? 'output' : 'success');
    },
  },

  listachievements: {
    syntax: 'listachievements',
    description: 'List all achievements with unlock status',
    handler(_args, con) {
      const list = window.__debug.listAchievements?.();
      if (!list) return con._print('Achievement system unavailable', 'error');
      for (const a of list) {
        const status = a.unlocked ? '✓' : `${a.progress}/${a.total}`;
        con._print(`  [${status}] ${a.id} — ${a.name}`, a.unlocked ? 'success' : 'output');
      }
      con._print(`Total: ${list.filter(a => a.unlocked).length}/${list.length} unlocked`, 'output');
    },
  },

  openachievements: {
    syntax: 'openachievements',
    description: 'Open the achievements panel',
    handler(_args, con) {
      window.__debug.openAchievements?.();
      con._print('Achievements panel opened', 'success');
    },
  },
  completequest: {
    syntax: 'completequest <quest_id>',
    description: 'Mark quest as complete',
    handler(args, con) {
      const [questId] = args;
      if (!questId) return con._print('Usage: completequest <quest_id>', 'error');
      const result = window.__debug.completeQuest(questId);
      con._print(result ?? `Quest completed: ${questId}`, 'success');
    },
  },
  failquest: {
    syntax: 'failquest <quest_id>',
    description: 'Fail a quest',
    handler(args, con) {
      const [questId] = args;
      if (!questId) return con._print('Usage: failquest <quest_id>', 'error');
      const result = window.__debug.failQuest(questId);
      con._print(result?.message ?? result ?? `Quest failed: ${questId}`, 'success');
    },
  },
  startquest: {
    syntax: 'startquest <quest_id>',
    description: 'Start a quest (alias: acceptquest)',
    handler(args, con) {
      const [questId] = args;
      if (!questId) return con._print('Usage: startquest <quest_id>', 'error');
      const result = window.__debug.acceptQuest(questId);
      con._print(result?.message ?? result ?? `Quest started: ${questId}`, 'success');
    },
  },

  acceptquest: {
    syntax: 'acceptquest <quest_id>',
    description: 'Accept a quest (alias: startquest)',
    handler(args, con) {
      const [questId] = args;
      if (!questId) return con._print('Usage: acceptquest <quest_id>', 'error');
      const result = window.__debug.acceptQuest(questId);
      con._print(result?.message ?? result ?? `Quest accepted: ${questId}`, 'success');
    },
  },

  getfame: {
    syntax: 'getfame',
    description: 'Show current fame, tier, and price modifier',
    handler(_args, con) {
      const summary = window.__debug.getFame();
      if (!summary) return con._print('Fame system unavailable', 'output');
      con._print(
        `Fame: ${summary.fame} | Tier: ${summary.tier} (${summary.label}) | Price modifier: ${summary.priceModifier}x`,
        'output'
      );
    },
  },
  addstatus: {
    syntax: 'addstatus <status_id> <char_name>',
    description: 'Apply status effect to character',
    handler(args, con) {
      const [statusId, charName] = args;
      if (!statusId || !charName) return con._print('Usage: addstatus <status_id> <char_name>', 'error');
      con._print(`addstatus: Command registered (game not yet loaded)`, 'output');
    },
  },
  revealmap: {
    syntax: 'revealmap',
    description: 'Remove all fog of war',
    handler(_args, con) {
      window.__debug.revealMap();
      con._print('Map revealed', 'success');
    },
  },
  hidemap: {
    syntax: 'hidemap',
    description: 'Restore fog of war',
    handler(_args, con) {
      window.__debug.hideMap();
      con._print('Map hidden', 'success');
    },
  },
  listparty: {
    syntax: 'listparty',
    description: 'Print party state to log',
    handler(_args, con) {
      const state = window.__debug.getPartyState();
      if (!state) return con._print('Party state unavailable (game not loaded)', 'output');
      con._print(JSON.stringify(state, null, 2), 'output');
    },
  },
  listinventory: {
    syntax: 'listinventory [char_name]',
    description: 'Print character inventory',
    handler(args, con) {
      const [charName] = args;
      const inv = window.__debug.getInventory(charName);
      if (!inv) return con._print('Inventory unavailable (game not loaded)', 'output');
      con._print(JSON.stringify(inv, null, 2), 'output');
    },
  },
  listenemy: {
    syntax: 'listenemy',
    description: 'Print current enemies',
    handler(_args, con) {
      const state = window.__debug.getCombatState();
      if (!state) return con._print('Combat state unavailable', 'output');
      con._print(JSON.stringify(state, null, 2), 'output');
    },
  },
  listquests: {
    syntax: 'listquests',
    description: 'Print quest log',
    handler(_args, con) {
      const quests = window.__debug.getQuests();
      if (!quests) return con._print('Quest log unavailable (game not loaded)', 'output');
      con._print(JSON.stringify(quests, null, 2), 'output');
    },
  },
  listskills: {
    syntax: 'listskills <char_name>',
    description: 'Print character skills',
    handler(args, con) {
      const [charName] = args;
      if (!charName) return con._print('Usage: listskills <char_name>', 'error');
      con._print(`listskills: Command registered (game not yet loaded)`, 'output');
    },
  },
  setscenario: {
    syntax: 'setscenario <scenario_id>',
    description: 'Jump to a specific test scenario',
    handler(args, con) {
      const [scenarioId] = args;
      if (!scenarioId) return con._print('Usage: setscenario <scenario_id>', 'error');
      const result = window.__debug.setScenario(scenarioId);
      con._print(result ?? `Scenario set: ${scenarioId}`, 'success');
    },
  },
  screenshot: {
    syntax: 'screenshot',
    description: 'Save screenshot with timestamp',
    handler(_args, con) {
      const result = window.__debug.screenshot();
      con._print(result ?? 'Screenshot saved', 'success');
    },
  },
  uploadlogs: {
    syntax: 'uploadlogs',
    description: 'Upload logs to server (requires consent)',
    handler(_args, con) {
      window.__debug.uploadLogs().then((ok) => {
        con._print(ok ? 'Logs uploaded successfully' : 'Upload failed or consent not given', ok ? 'success' : 'error');
      });
    },
  },
  verbose: {
    syntax: 'verbose <on|off>',
    description: 'Toggle verbose logging',
    handler(args, con) {
      const [onOff] = args;
      if (onOff !== 'on' && onOff !== 'off') return con._print('Usage: verbose <on|off>', 'error');
      DebugLog.setVerbose(onOff === 'on');
      con._print(`Verbose mode ${onOff}`, 'success');
    },
  },
  town: {
    syntax: 'town [town_name]',
    description: 'Open the TownMenuPanel with sample or named town data',
    handler(args, con) {
      const openFn = window.__openTownMenu;
      if (!openFn) return con._print('TownMenuPanel not initialized', 'error');
      const name = args.join(' ') || 'Ironhold';
      openFn({
        name,
        type: 'trading_post',
        services: ['blacksmith', 'general_store', 'inn', 'tavern', 'temple', 'party'],
      });
      con._print(`Opened town menu: ${name}`, 'success');
    },
  },
  entertown: {
    syntax: 'entertown <town_type_id>',
    description: 'Enter a town of a given archetype for testing',
    handler(args, con) {
      const [townTypeId] = args;
      if (!townTypeId) return con._print('Usage: entertown <town_type_id>', 'error');
      const result = window.__debug.enterTown(townTypeId);
      con._print(result ?? `Entered town of type: ${townTypeId}`, 'success');
    },
  },
  exittown: {
    syntax: 'exittown',
    description: 'Exit the current town',
    handler(_args, con) {
      const result = window.__debug.exitTown();
      con._print(result ?? 'Exited town', 'success');
    },
  },
  gettownstate: {
    syntax: 'gettownstate',
    description: 'Print current town state',
    handler(_args, con) {
      const state = window.__debug.getTownState();
      if (!state) return con._print('Not currently in a town', 'output');
      con._print(JSON.stringify(state, null, 2), 'output');
    },
  },
  camp: {
    syntax: 'camp [charId:role ...]',
    description: 'Set up camp with optional role assignments (e.g. camp hero1:scout hero2:cook)',
    handler(args, con) {
      // Parse "charId:role" pairs from args
      const assignments = args
        .filter(a => a.includes(':'))
        .map(a => {
          const [charId, role] = a.split(':');
          return { charId: charId?.trim(), role: role?.trim() };
        });
      const result = window.__debug.camp(assignments);
      con._print(result?.message ?? JSON.stringify(result), 'success');
    },
  },

  rest: {
    syntax: 'rest',
    description: 'Rest until morning (consumes rations, may trigger night encounter)',
    handler(_args, con) {
      const result = window.__debug.restUntilMorning();
      if (!result) return con._print('Camping system unavailable', 'error');
      con._print(result.message, result.encounter ? 'error' : 'success');
      if (result.warnings.length) {
        for (const w of result.warnings) con._print(`  Warning: ${w}`, 'error');
      }
    },
  },

  forage: {
    syntax: 'forage',
    description: 'Attempt foraging to gain ration points',
    handler(_args, con) {
      const result = window.__debug.forage();
      if (!result) return con._print('Camping system unavailable', 'error');
      con._print(result.message, result.success ? 'success' : 'output');
    },
  },

  assignplayer: {
    syntax: 'assignplayer <playerId 1-4> <characterId>',
    description: 'Assign a character to a player slot',
    handler(args, con) {
      const [pidStr, charId] = args;
      if (!pidStr || !charId) return con._print('Usage: assignplayer <playerId 1-4> <characterId>', 'error');
      const result = window.__debug.assignPlayer(parseInt(pidStr, 10), charId);
      con._print(result ?? `Player ${pidStr} assigned character ${charId}`, 'success');
    },
  },

  setnavigator: {
    syntax: 'setnavigator <playerId 1-4>',
    description: 'Set the Navigator player for world map movement',
    handler(args, con) {
      const [pidStr] = args;
      if (!pidStr) return con._print('Usage: setnavigator <playerId 1-4>', 'error');
      const result = window.__debug.setNavigator(parseInt(pidStr, 10));
      con._print(result ?? `Navigator set to player ${pidStr}`, 'success');
    },
  },

  setplayers: {
    syntax: 'setplayers <1-4>',
    description: 'Set the number of active players',
    handler(args, con) {
      const [nStr] = args;
      if (!nStr) return con._print('Usage: setplayers <1-4>', 'error');
      const result = window.__debug.setPlayerCount(parseInt(nStr, 10));
      con._print(result ?? `Active player count set to ${nStr}`, 'success');
    },
  },

  getcoopstate: {
    syntax: 'getcoopstate',
    description: 'Print current co-op assignments, navigator, and turn info',
    handler(_args, con) {
      const state = window.__debug.getCoopState();
      if (!state) return con._print('CoopSystem unavailable', 'output');
      con._print(JSON.stringify(state, null, 2), 'output');
    },
  },

  addvehicle: {
    syntax: 'addvehicle <horse|donkey|wagon>',
    description: 'Add a vehicle to the party',
    handler(args, con) {
      const [vehicleId] = args;
      if (!vehicleId) return con._print('Usage: addvehicle <horse|donkey|wagon>', 'error');
      const result = window.__debug.addVehicle(vehicleId);
      con._print(result?.message ?? JSON.stringify(result), result?.success ? 'success' : 'error');
    },
  },

  setfog: {
    syntax: 'setfog <on|off>',
    description: 'Enable or disable fog of war (Beta)',
    handler(args, con) {
      const [onOff] = args;
      if (onOff !== 'on' && onOff !== 'off') return con._print('Usage: setfog <on|off>', 'error');
      window.__settings = window.__settings ?? {};
      window.__settings.fogOfWar = (onOff === 'on');
      // Trigger a fog redraw in the active scene if available
      EventBus.emit('fog:update', {});
      con._print(`Fog of war ${onOff === 'on' ? 'enabled' : 'disabled'}`, 'success');
    },
  },

  prestige: {
    syntax: 'prestige [bonus_id]',
    description: 'Open prestige panel or trigger prestige with a specific bonus id',
    handler(args, con) {
      const ps = window.__prestigeSystem;
      if (!ps) return con._print('PrestigeSystem not initialized', 'error');

      const [bonusId] = args;

      if (!bonusId) {
        // Just open the prestige panel
        const panel = window.__prestigePanel;
        if (panel) {
          panel.open();
          con._print('Prestige panel opened', 'success');
        } else {
          // Print summary instead
          const summary = ps.getSummary();
          con._print(JSON.stringify(summary, null, 2), 'output');
        }
        return;
      }

      // Trigger prestige with the specified bonus
      const bonuses = ps.getAvailableBonuses();
      // Also allow any bonus from the full pool by searching
      const allBonuses = [
        'veteran_start', 'gold_reserve', 'heirloom_weapon', 'fame_head_start',
        'bonus_skill_point', 'swift_travel', 'fortified_start', 'merchant_friend',
        'lucky_drops', 'veteran_companions',
      ];
      const bonusObj = bonuses.find(b => b.id === bonusId)
        ?? (allBonuses.includes(bonusId) ? { id: bonusId, name: bonusId, description: '(debug override)' } : null);

      if (!bonusObj) {
        return con._print(`Unknown bonus id: "${bonusId}". Valid ids: ${allBonuses.join(', ')}`, 'error');
      }

      const result = ps.doPrestige(bonusObj);
      con._print(result.message, result.ok ? 'success' : 'error');
    },
  },

  getprestige: {
    syntax: 'getprestige',
    description: 'Show current prestige level and active bonuses',
    handler(_args, con) {
      const ps = window.__prestigeSystem;
      if (!ps) return con._print('PrestigeSystem not initialized', 'error');
      const summary = ps.getSummary();
      con._print(`Prestige level: ${summary.prestige}`, 'output');
      con._print(`Active bonus: ${summary.latestBonus ?? 'none'}`, 'output');
      if (summary.bonuses.length > 1) {
        con._print(`All bonuses: ${summary.bonuses.join(', ')}`, 'output');
      }
    },
  },

  setxp: {
    syntax: 'setxp <amount> [char_name]',
    description: 'Set total XP for a character (triggers level-up if threshold crossed)',
    handler(args, con) {
      const [amount, charName] = args;
      if (amount === undefined) return con._print('Usage: setxp <amount> [char_name]', 'error');
      const result = window.__debug.setXP(parseInt(amount, 10), charName);
      if (result && typeof result.then === 'function') {
        result.then(msg => con._print(msg, 'success')).catch(err => con._print(err.message, 'error'));
      } else {
        con._print(result ?? `XP set to ${amount}`, 'success');
      }
    },
  },

  getstats: {
    syntax: 'getstats [char_name]',
    description: 'Print character stats summary (level, XP, HP, attributes)',
    handler(args, con) {
      const [charName] = args;
      const stats = window.__debug.getStats(charName);
      if (!stats) return con._print('No hero character found (game not loaded or no character created)', 'output');
      con._print(JSON.stringify(stats, null, 2), 'output');
    },
  },

  help: {
    syntax: 'help',
    description: 'List all commands',
    handler(_args, con) {
      const lines = Object.entries(COMMANDS).map(([name, cmd]) =>
        `  ${cmd.syntax.padEnd(42)} — ${cmd.description}`
      );
      con._print('Available commands:\n' + lines.join('\n'), 'output');
    },
  },
};

// ─── DebugConsole ────────────────────────────────────────────────────────────

const DebugConsole = {
  /** @type {HTMLElement|null} */
  _el: null,

  /** @type {HTMLElement|null} */
  _output: null,

  /** @type {HTMLInputElement|null} */
  _input: null,

  /** @type {boolean} */
  _open: false,

  /** @type {string[]} Command history */
  _history: [],

  /** @type {number} Current history navigation index */
  _historyIndex: -1,

  /**
   * Initialize the debug console DOM and bind keyboard shortcut.
   */
  init() {
    this._buildDOM();
    this._bindKeys();
    this._setupWindowDebug();

    // Listen for debug toggle from InputManager
    EventBus.on('input:debug', () => this.toggle());

    DebugLog.info('[debug] DebugConsole initialized');
  },

  // ─── DOM construction ────────────────────────────────────────────────────

  _buildDOM() {
    const el = document.createElement('div');
    el.id = 'debug-console';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Debug Console');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML = `
      <div class="debug-console-header">
        <span class="debug-console-title">SPELLROADS DEBUG CONSOLE v${window.__gameVersion ?? '?'}</span>
        <span class="debug-console-hint">~ to close</span>
        <button class="debug-console-close" aria-label="Close debug console" tabindex="0">×</button>
      </div>
      <div class="debug-console-output" id="debug-console-output" aria-live="polite" aria-relevant="additions"></div>
      <div class="debug-console-input-row">
        <span class="debug-console-prompt">&gt;</span>
        <input
          type="text"
          class="debug-console-input"
          id="debug-console-input"
          placeholder="Type a command — 'help' for list"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
        />
      </div>
    `;

    document.body.appendChild(el);
    this._el = el;
    this._output = el.querySelector('#debug-console-output');
    this._input  = el.querySelector('#debug-console-input');

    el.querySelector('.debug-console-close').addEventListener('click', () => this.hide());

    this._input.addEventListener('keydown', (e) => this._onInputKey(e));

    this._print('Debug console ready. Type "help" for commands.', 'info');
  },

  _bindKeys() {
    window.addEventListener('keydown', (e) => {
      // Tilde / backtick
      if (e.code === 'Backquote' || e.key === '`' || e.key === '~') {
        e.preventDefault();
        this.toggle();
      }
      // Escape closes console if open
      if (e.key === 'Escape' && this._open) {
        e.stopPropagation();
        this.hide();
      }
    });
  },

  _onInputKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const raw = this._input.value.trim();
      if (raw) {
        this._history.unshift(raw);
        if (this._history.length > 50) this._history.pop();
        this._historyIndex = -1;
        this._execute(raw);
      }
      this._input.value = '';
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(this._historyIndex + 1, this._history.length - 1);
      this._historyIndex = next;
      this._input.value = this._history[next] ?? '';
      // Move cursor to end
      requestAnimationFrame(() => {
        this._input.selectionStart = this._input.selectionEnd = this._input.value.length;
      });
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = this._historyIndex - 1;
      this._historyIndex = next;
      this._input.value = next >= 0 ? (this._history[next] ?? '') : '';
    }

    // Tab autocomplete
    if (e.key === 'Tab') {
      e.preventDefault();
      const partial = this._input.value.trim().toLowerCase();
      const matches = Object.keys(COMMANDS).filter(cmd => cmd.startsWith(partial));
      if (matches.length === 1) {
        this._input.value = matches[0] + ' ';
      } else if (matches.length > 1) {
        this._print(`Matches: ${matches.join('  ')}`, 'info');
      }
    }
  },

  // ─── Command execution ────────────────────────────────────────────────────

  /**
   * Parse and execute a raw command string.
   * @param {string} raw
   */
  _execute(raw) {
    this._print(raw, 'cmd');
    DebugLog.event(`[debug] Console command: ${raw}`);

    const parts = raw.trim().split(/\s+/);
    const cmdName = parts[0].toLowerCase();
    const args = parts.slice(1);

    const cmd = COMMANDS[cmdName];
    if (!cmd) {
      this._print(`Unknown command: "${cmdName}". Type "help" for list.`, 'error');
      return;
    }

    try {
      cmd.handler(args, this);
    } catch (err) {
      this._print(`Error: ${err.message}`, 'error');
      DebugLog.error(`[debug] Console command error: ${err.message}`);
    }
  },

  // ─── Output ──────────────────────────────────────────────────────────────

  /**
   * Print a line to the console output.
   * @param {string} text
   * @param {'cmd'|'output'|'error'|'info'|'success'} type
   */
  _print(text, type = 'output') {
    if (!this._output) return;

    const line = document.createElement('div');
    line.className = `debug-line debug-${type}`;
    line.textContent = text;
    this._output.appendChild(line);

    // Trim output to last 500 lines
    while (this._output.children.length > 500) {
      this._output.removeChild(this._output.firstChild);
    }

    this._output.scrollTop = this._output.scrollHeight;
  },

  // ─── Visibility ──────────────────────────────────────────────────────────

  show() {
    if (!this._el) return;
    this._el.classList.add('visible');
    this._open = true;
    requestAnimationFrame(() => this._input?.focus());
    DebugLog.info('[debug] Console opened');
  },

  hide() {
    if (!this._el) return;
    this._el.classList.remove('visible');
    this._open = false;
    DebugLog.info('[debug] Console closed');
  },

  toggle() {
    this._open ? this.hide() : this.show();
  },

  isOpen() {
    return this._open;
  },

  // ─── window.__debug API ──────────────────────────────────────────────────

  /**
   * Populate window.__debug with all API methods.
   * Called by init(). Stubs that become real once game systems load.
   */
  _setupWindowDebug() {
    const self = this;

    window.__debug = {
      // ── State queries ───────────────────────────────────────────────────

      /** @returns {{ characters, gold, rations, fame, alignment, region }|null} */
      getPartyState() {
        return window.__debugGetPartyState?.() ?? null;
      },

      /** @param {string} nameOrId */
      getCharacterState(nameOrId) {
        return window.__debugGetCharacterState?.(nameOrId) ?? null;
      },

      /** @param {number} playerId */
      getPlayerState(playerId) {
        return window.__debugGetPlayerState?.(playerId) ?? null;
      },

      getRegion() {
        return window.__debugGetRegion?.() ?? null;
      },

      getQuests() {
        return QuestSystem.serialize();
      },

      /** @param {string} [nameOrId] */
      getInventory(nameOrId) {
        return window.__debugGetInventory?.(nameOrId) ?? null;
      },

      getCombatState() {
        return window.__debugGetCombatState?.() ?? null;
      },

      /**
       * Returns all error-level log entries.
       * Wired directly to DebugLog.
       * @returns {Array<object>}
       */
      getErrors() {
        return DebugLog.getErrors();
      },

      /**
       * Returns last N log entries.
       * @param {number} [n=50]
       * @returns {Array<object>}
       */
      getLogEntries(n = 50) {
        return DebugLog.getLast(n);
      },

      // ── Commands ────────────────────────────────────────────────────────

      spawn(enemyId, count = 1) {
        return window.__debugSpawn?.(enemyId, count) ?? 'Command registered (game not yet loaded)';
      },

      give(itemId, qty = 1, charNameOrId) {
        return window.__debugGive?.(itemId, qty, charNameOrId) ?? 'Command registered (game not yet loaded)';
      },

      setLevel(n, charNameOrId) {
        return window.__debugSetLevel?.(n, charNameOrId) ?? 'Command registered (game not yet loaded)';
      },

      teleport(locationId) {
        return window.__debugTeleport?.(locationId) ?? 'Command registered (game not yet loaded)';
      },

      setTime(hour) {
        return window.__debugSetTime?.(hour) ?? 'Command registered (game not yet loaded)';
      },

      triggerEvent(eventId) {
        // Try WorldEventSystem first, then fall back to encounter trigger
        const wes = window.__worldEventSystem;
        if (wes) return wes.triggerEvent(eventId);
        return window.__debugTriggerEvent?.(eventId) ?? 'Command registered (game not yet loaded)';
      },

      setFame(n) {
        const parsed = parseInt(n, 10);
        if (isNaN(parsed)) return 'Invalid fame value';
        const delta = parsed - FameSystem.getFame();
        const newFame = FameSystem.addFame(delta);
        const summary = FameSystem.getSummary();
        return `Fame set to ${newFame} (${summary.tier})`;
      },

      getFame() {
        return FameSystem.getSummary();
      },

      completeQuest(questId) {
        const result = QuestSystem.completeQuest(questId);
        return result.message;
      },

      failQuest(questId) {
        const result = QuestSystem.failQuest(questId);
        return result.message;
      },

      acceptQuest(questId) {
        const result = QuestSystem.acceptQuest(questId);
        return result;
      },

      startQuest(questId) {
        const result = QuestSystem.acceptQuest(questId);
        return result;
      },

      listQuests() {
        return QuestSystem.getAllTemplates();
      },

      revealMap() {
        return window.__debugRevealMap?.() ?? 'Command registered (game not yet loaded)';
      },

      hideMap() {
        return window.__debugHideMap?.() ?? 'Command registered (game not yet loaded)';
      },

      setScenario(scenarioId) {
        return window.__debugSetScenario?.(scenarioId) ?? 'Command registered (game not yet loaded)';
      },

      screenshot() {
        return window.__debugScreenshot?.() ?? 'Command registered (game not yet loaded)';
      },

      async uploadLogs() {
        return await DebugLog.upload();
      },

      // ── Input simulation (Playwright) ────────────────────────────────────

      /**
       * Simulate an input action for Playwright tests.
       * @param {number} playerId
       * @param {string} actionName
       */
      simulateInput(playerId, actionName) {
        // Imported lazily to avoid circular dependency at module load time
        window.__inputManager?.simulateAction(playerId, actionName);
        DebugLog.event(`[debug] simulateInput; playerId=${playerId} action=${actionName}`);
      },

      // ── World / map debug commands ───────────────────────────────────────

      revealmap() {
        WorldState.revealAll();
        EventBus.emit('fog:update');
      },

      hidemap() {
        WorldState.hideAll();
        EventBus.emit('fog:update');
      },

      gotoWorld() {
        // Imported lazily via dynamic import to avoid loading SceneManager at boot
        import('../core/SceneManager.js').then(m => m.default.transition('worldMap'));
      },

      gotoRegion(id) {
        const regionId = id ?? WorldState.currentRegionId;
        import('../core/SceneManager.js').then(m => m.default.transition('regionMap', { regionId }));
      },

      gotoLocal(id) {
        const regionId = id ?? WorldState.currentRegionId;
        import('../core/SceneManager.js').then(m => m.default.transition('localMap', { regionId }));
      },

      /**
       * Navigate to any registered scene by name, passing optional data.
       * Used by Playwright tests to jump directly into any scene.
       * @param {string} sceneName
       * @param {object} [data={}]
       */
      gotoScene(sceneName, data = {}) {
        import('../core/SceneManager.js').then(m => m.default.transition(sceneName, data));
      },

      /**
       * Navigate directly to a dungeon interior.
       * @param {string} [dungeonType='burial_mound']
       * @param {number} [regionLevel=1]
       * @param {number} [seed=42]
       */
      gotoDungeon(dungeonType = 'burial_mound', regionLevel = 1, seed = 42) {
        import('../core/SceneManager.js').then(m =>
          m.default.transition('dungeon', { dungeonType, regionLevel, seed })
        );
      },

      // ── Combat debug commands ────────────────────────────────────────────────

      /**
       * Spawn a test combat encounter with the given enemy type.
       * Requires at least one hero character to exist.
       * @param {string} [enemyId='orc_grunt']
       * @param {number} [count=2]
       * @returns {string} Status message
       */
      startCombat(enemyId, count) {
        import('../systems/CharacterSystem.js').then(({ default: CS }) => {
          import('../systems/EnemySystem.js').then(({ default: ES }) => {
            import('../core/SceneManager.js').then(({ default: SM }) => {
              const partyChars = CS.getAll().filter(c => c.isHero).slice(0, 4);
              if (!partyChars.length) {
                console.warn('[debug] No heroes — create a character first');
                return;
              }
              const eid = enemyId || 'orc_grunt';
              const cnt = count || 2;
              const enemies = [];
              for (let i = 0; i < cnt; i++) {
                const e = ES.createInstance(eid, 1);
                if (e) enemies.push(e);
              }
              SM.transition('combat', { partyChars, enemies, environment: 'forest', regionLevel: 1 });
            });
          });
        });
        return `Starting combat vs ${count ?? 2}x ${enemyId ?? 'orc_grunt'}...`;
      },

      /**
       * Set XP on the first hero character (or a named character) and trigger level-up checks.
       * Used by Playwright balance tests and the debug console.
       * @param {number} amount — Total XP to set (absolute, not additive)
       * @param {string} [charNameOrId] — Optional: target a specific character by name or id
       * @returns {string} Status message
       */
      setXP(amount, charNameOrId) {
        return import('../systems/CharacterSystem.js').then(({ default: CS }) => {
          let chars = CS.getAll().filter(c => c.isHero);
          if (charNameOrId) {
            chars = chars.filter(c => c.id === charNameOrId || c.name.toLowerCase() === String(charNameOrId).toLowerCase());
          } else {
            chars = chars.slice(0, 1);
          }
          if (!chars.length) return 'No hero characters found';
          for (const char of chars) {
            char.xp = 0;
            char.level = 1;
            CS.grantXP(char, Math.max(0, parseInt(amount, 10) || 0));
          }
          const c = chars[0];
          return `XP set to ${c.xp} (level ${c.level}) for ${c.name}`;
        }).catch(err => `setXP error: ${err.message}`);
      },

      /**
       * Return a summary of the first hero's stats for balance verification.
       * Returns: { name, level, xp, hp, maxHP, mana, maxMana, attributes, mightMod, enduranceMod, class, race }.
       * @param {string} [charNameOrId]
       * @returns {object|null}
       */
      getStats(charNameOrId) {
        const allChars = window.__debugGetCharacterState?.() ?? [];
        const chars = Array.isArray(allChars) ? allChars : [allChars];
        let char = chars.find(c => c?.isHero);
        if (charNameOrId) {
          char = chars.find(c => c?.id === charNameOrId || c?.name?.toLowerCase() === String(charNameOrId).toLowerCase()) ?? char;
        }
        if (!char) return null;
        const enduranceMod = Math.floor(((char.attributes?.endurance ?? 10) - 10) / 2);
        const mightMod = Math.floor(((char.attributes?.might ?? 10) - 10) / 2);
        return {
          name: char.name,
          level: char.level ?? 1,
          xp: char.xp ?? 0,
          hp: char.hp ?? 0,
          maxHP: char.maxHP ?? 0,
          mana: char.mana ?? 0,
          maxMana: char.maxMana ?? 0,
          attributes: { ...(char.attributes ?? {}) },
          mightMod,
          enduranceMod,
          class: char.class ?? 'Adventurer',
          race: char.race ?? 'human',
        };
      },

      /**
       * Get the current combat state object.
       * @returns {object|null}
       */
      getCombatState() {
        return window.__combatSystem?.getState() ?? null;
      },

      /**
       * Execute an attack against a target in the current combat.
       * @param {string} targetId
       * @returns {object|undefined}
       */
      attackTarget(targetId) {
        return window.__combatSystem?.executeAttack(targetId);
      },

      /**
       * Advance to the next turn in the current combat.
       * @returns {object|null}
       */
      nextTurn() {
        return window.__combatSystem?.nextTurn();
      },

      // ── Town debug commands ──────────────────────────────────────────────────

      /**
       * Enter a town of the given archetype type for testing.
       * Generates a minimal town instance and sets WorldState.currentTown.
       * @param {string} townTypeId — e.g. 'human_town', 'dvergr_hold'
       * @returns {string} Status message
       */
      enterTown(townTypeId) {
        return import('../systems/TownSystem.js').then(({ default: TownSystem }) => {
          TownSystem.init();
          const archetype = TownSystem.getArchetype(townTypeId);
          if (!archetype) {
            return `Unknown town type: "${townTypeId}". Valid types: human_town, dvergr_hold, alfar_grove, tomte_village, goblin_fortress, port_town, trade_hub`;
          }

          const townData = {
            id: 9999,
            townType: townTypeId,
            name: `Debug ${archetype.name}`,
            level: WorldState.currentRegionLevel ?? 1,
            regionId: WorldState.currentRegionId,
          };

          const town = TownSystem.generateTown(townData, 12345);
          if (!town) {
            return `Failed to generate town of type "${townTypeId}"`;
          }

          WorldState.enterTown(town);
          DebugLog.info(`[debug] enterTown; type=${townTypeId} name="${town.name}"`);
          return `Entered "${town.name}" (type=${townTypeId}, level=${town.level})`;
        }).catch(err => {
          DebugLog.error(`[debug] enterTown error: ${err.message}`);
          return `Error entering town: ${err.message}`;
        });
      },

      /**
       * Exit the current town.
       * @returns {string} Status message
       */
      exitTown() {
        const townName = WorldState.currentTown?.name ?? null;
        if (!townName) {
          return 'Not currently in a town.';
        }
        WorldState.exitTown();
        DebugLog.info('[debug] exitTown');
        return `Exited "${townName}"`;
      },

      /**
       * Return the current town state object.
       * @returns {object|null}
       */
      getTownState() {
        const town = WorldState.currentTown;
        if (!town) return null;

        // Return a clean summary without the full shop inventory instances
        return {
          id: town.id,
          name: town.name,
          townType: town.townType,
          race: town.race,
          level: town.level,
          regionId: town.regionId,
          availableShops: town.availableShops,
          innCost: town.innCost,
          requiredFame: town.requiredFame,
          shopPriceModifier: town.shopPriceModifier,
          shopItemCounts: Object.fromEntries(
            Object.entries(town.shops ?? {}).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0])
          ),
          haggledToday: WorldState.haggledToday,
        };
      },

      // ── Camping debug commands ───────────────────────────────────────────────

      /**
       * Set up camp with optional role assignments.
       * @param {Array<{charId: string, role: string}>} [roleAssignments]
       * @param {object} [options] — { hasTent, hasAlarmWire, hasPalisade }
       * @returns {{ success: boolean, message: string, campState: object }}
       */
      camp(roleAssignments = [], options = {}) {
        const result = CampingSystem.setupCamp(roleAssignments, options);
        DebugLog.info(`[debug] camp; assignments=${roleAssignments.length}`);
        return result;
      },

      /**
       * Rest until morning using a seeded RNG fork.
       * @returns {{ rationCost, healed, encounter, message, warnings }|null}
       */
      restUntilMorning() {
        // Build a minimal RNG compatible with CampingSystem expectations
        const debugRng = {
          nextFloat: () => Math.random(),
          nextInt:   (min, max) => min + Math.floor(Math.random() * (max - min + 1)),
          nextBool:  (chance) => Math.random() < chance,
          pick:      (arr) => arr[Math.floor(Math.random() * arr.length)],
          fork:      function() { return { ...this }; },
        };

        const result = CampingSystem.restUntilMorning(debugRng);
        DebugLog.info(`[debug] restUntilMorning; result=${JSON.stringify(result)}`);
        return result;
      },

      /**
       * Attempt foraging in the current biome.
       * @returns {{ success, gained, roll, dc, message }|null}
       */
      forage() {
        const debugRng = {
          nextFloat: () => Math.random(),
          nextInt:   (min, max) => min + Math.floor(Math.random() * (max - min + 1)),
          nextBool:  (chance) => Math.random() < chance,
          pick:      (arr) => arr[Math.floor(Math.random() * arr.length)],
          fork:      function() { return { ...this }; },
        };

        // Gather party characters safely
        let partyChars = [];
        try {
          partyChars = window.__characterSystem?.getAll?.() ?? [];
        } catch { /* system not loaded */ }

        const result = CampingSystem.forage(partyChars, debugRng);
        DebugLog.info(`[debug] forage; result=${JSON.stringify(result)}`);
        return result;
      },

      /**
       * Get current camp state.
       * @returns {object|null}
       */
      getCampState() {
        return CampingSystem.getCampState();
      },

      // ── Vehicle debug commands ───────────────────────────────────────────────

      /**
       * Add a vehicle to the party.
       * @param {string} vehicleId — 'horse', 'donkey', or 'wagon'
       * @returns {{ success: boolean, message: string, instance: object|null }}
       */
      addVehicle(vehicleId) {
        const result = VehicleSystem.addVehicle(vehicleId);
        DebugLog.info(`[debug] addVehicle; vehicleId=${vehicleId} success=${result.success}`);
        return result;
      },

      /**
       * Get all party vehicles.
       * @returns {Array<{ id: string, vehicleId: string, broken: boolean }>}
       */
      getVehicles() {
        return VehicleSystem.getVehicles();
      },

      /**
       * Get current party ration points.
       * @returns {number}
       */
      getRations() {
        return WorldState.rationPoints ?? 0;
      },

      // ── Co-op debug commands ─────────────────────────────────────────────────

      /**
       * Assign a character to a player slot.
       * @param {number} playerId — 1–4
       * @param {string} characterId
       * @returns {string} Status message
       */
      assignPlayer(playerId, characterId) {
        return import('../systems/CoopSystem.js').then(({ default: CoopSystem }) => {
          const ok = CoopSystem.assignCharacter(playerId, characterId);
          return ok
            ? `Player ${playerId} assigned to character "${characterId}"`
            : `Failed to assign player ${playerId} to character "${characterId}" — check IDs`;
        }).catch(err => `CoopSystem error: ${err.message}`);
      },

      /**
       * Set the Navigator player.
       * @param {number} playerId — 1–4
       * @returns {string} Status message
       */
      setNavigator(playerId) {
        return import('../systems/CoopSystem.js').then(({ default: CoopSystem }) => {
          CoopSystem.setNavigator(playerId);
          return `Navigator set to player ${playerId}`;
        }).catch(err => `CoopSystem error: ${err.message}`);
      },

      /**
       * Set the number of active players.
       * @param {number} n — 1–4
       * @returns {string} Status message
       */
      setPlayerCount(n) {
        return import('../systems/CoopSystem.js').then(({ default: CoopSystem }) => {
          CoopSystem.setPlayerCount(n);
          return `Active player count set to ${CoopSystem.getPlayerCount()}`;
        }).catch(err => `CoopSystem error: ${err.message}`);
      },

      /**
       * Return a snapshot of the current co-op state.
       * @returns {Promise<object>}
       */
      getCoopState() {
        return import('../systems/CoopSystem.js').then(({ default: CoopSystem }) => {
          const assignments = CoopSystem.getAssignments();
          const navigator = CoopSystem.getNavigator();
          const playerCount = CoopSystem.getPlayerCount();
          const isPlayerTurn = {};
          for (let pid = 1; pid <= 4; pid++) {
            isPlayerTurn[pid] = CoopSystem.isPlayerTurn(pid);
          }
          return { assignments, navigator, playerCount, isPlayerTurn };
        }).catch(err => {
          DebugLog.error(`[debug] getCoopState error: ${err.message}`);
          return null;
        });
      },

      // ── Starting scenario commands ────────────────────────────────────────────

      /**
       * Return all starting scenarios (locked and unlocked).
       * Reads from window.__startingScenarios which is populated in main.js.
       * @returns {Array<object>}
       */
      getStartingScenarios() {
        return window.__startingScenarios ?? [];
      },

      /**
       * Return only unlocked starting scenarios.
       * @returns {Array<object>}
       */
      getUnlockedScenarios() {
        const all = window.__startingScenarios ?? [];
        return all.filter(s => s.unlocked);
      },

      // ── Save system ──────────────────────────────────────────────────────────

      async saveGame(slot) {
        const ss = window.__saveSystem;
        if (!ss) return 'SaveSystem not initialized';
        const r = await ss.save(slot ?? 'auto');
        return r.message;
      },

      async loadGame(slot) {
        const ss = window.__saveSystem;
        if (!ss) return 'SaveSystem not initialized';
        const r = await ss.load(slot ?? 'auto');
        if (r.ok) {
          // Trigger achievement for loading a save
          window.__achievementSystem?.trigger('game_loaded');
        }
        return r.message;
      },

      getSaveSlots() {
        return window.__saveSystem?.getSlotInfo() ?? [];
      },

      exportSave() {
        window.__saveSystem?.exportToFile();
        return 'Exporting save to file…';
      },

      // ── Achievements ─────────────────────────────────────────────────────────

      getAchievements() {
        return window.__achievementSystem?.getAll() ?? [];
      },

      getUnlockedAchievements() {
        return window.__achievementSystem?.getUnlocked() ?? [];
      },

      triggerAchievement(triggerId, ctx) {
        window.__achievementSystem?.trigger(triggerId, ctx ?? {});
        return `Triggered: ${triggerId}`;
      },

      // ── Compendium ───────────────────────────────────────────────────────────

      openCompendium() {
        window.__compendiumPanel?.open();
        return 'Compendium opened';
      },

      // ── Skill trees ──────────────────────────────────────────────────────────

      /**
       * Returns all skill tree definitions from the data file.
       * @returns {Promise<Array<object>>}
       */
      async getSkillTrees() {
        const { default: trees } = await import('../data/skill_trees.json', { assert: { type: 'json' } })
          .catch(() => import('../data/skill_trees.json'));
        return trees;
      },

      // ── Prestige ─────────────────────────────────────────────────────────────

      /**
       * Returns the current prestige state summary.
       * @returns {{ prestige: number, bonuses: string[], latestBonus: string|null }|null}
       */
      getPrestigeState() {
        return window.__prestigeSystem?.getSummary() ?? null;
      },

      /**
       * Trigger prestige with a bonus id (skips eligibility check — for testing).
       * @param {string} bonusId
       * @returns {{ ok: boolean, message: string }}
       */
      doPrestige(bonusId) {
        const ps = window.__prestigeSystem;
        if (!ps) return { ok: false, message: 'PrestigeSystem not initialized' };
        const bonus = { id: bonusId, name: bonusId, description: '(debug)' };
        return ps.doPrestige(bonus);
      },

      /**
       * Check if the party can prestige (avg level >= 20).
       * @returns {boolean}
       */
      canPrestige() {
        return window.__prestigeSystem?.canPrestige() ?? false;
      },
    };

    // Also expose DebugLog globally
    window.__debugLog = DebugLog;
  },
};

export default DebugConsole;
